import { Link } from "react-router-dom";
import BrandMark, { TAGLINE } from "@/components/BrandMark";
import {
  ArrowUpRight,
  Compass,
  HeartHalf,
  ChartLineUp,
  ShieldCheck,
} from "@phosphor-icons/react";

const concepts = [
  {
    id: "a",
    num: "01",
    eyebrow: "Concept 01 · Confidence under pressure",
    title: "Executive Assurance",
    summary:
      "Calm, high-trust advisory partner for leaders managing consequential people and culture decisions. Built for buyers who need to feel they are bringing in mature judgment, discretion, and strategic steadiness.",
    role: "Institutional trust-building",
    palette: ["#FFFFF0", "#F5F5DC", "#1A362D", "#B8838B"],
    fonts: "Cormorant Garamond · Manrope",
    path: "/concept-a",
    accent: "bg-[#1A362D]",
    text: "text-[#1A362D]",
    tile: "bg-[#FFFFF0] border border-[#1A362D]/15",
    Icon: Compass,
  },
  {
    id: "b",
    num: "02",
    eyebrow: "Concept 02 · Structure & strategic clarity",
    title: "Systems That Hold",
    summary:
      "Strategic partner organizations call when the human side of the institution needs structure, alignment, and durable support. Pathway-oriented offer architecture — not personality-led advisory.",
    role: "Offer clarity & scalability",
    palette: ["#F4F1EA", "#E3DCC8", "#0F3A2E", "#B8838B"],
    fonts: "Bricolage Grotesque · General Sans",
    path: "/concept-b",
    accent: "bg-[#0F3A2E]",
    text: "text-[#0F3A2E]",
    tile: "bg-[#F4F1EA] border border-[#0F3A2E]/15",
    Icon: HeartHalf,
  },
  {
    id: "c",
    num: "03",
    eyebrow: "Concept 03 · Trust & relational credibility",
    title: "Human-Centered Authority",
    summary:
      "Intersection of warmth, discernment, and executive credibility. Greater strategic weight to Katrina’s presence as authority layer, while Faith\u00A01st remains the central container.",
    role: "High-trust differentiation",
    palette: ["#FBF7F0", "#EFE3D2", "#2C4A3E", "#B8838B"],
    fonts: "Fraunces · Karla",
    path: "/concept-c",
    accent: "bg-[#2C4A3E]",
    text: "text-[#2C4A3E]",
    tile: "bg-[#FBF7F0] border border-[#2C4A3E]/15",
    Icon: ChartLineUp,
  },
  {
    id: "d",
    num: "04",
    eyebrow: "Concept 04 · Synthesis & launch direction",
    title: "Synthesis",
    summary:
      "The recommended launch direction: structural pathway clarity from Concept 02, founder-led trust from Concept 03, and the executive composure of Concept 01 — built against the final Faith 1st Executive Advisory brand identity system (Cormorant Garamond + Inter, Deep Teal / Warm Cream / Warm Ivory / Charcoal / Warm Gold).",
    role: "Launch-ready synthesis",
    palette: ["#FBF7F0", "#F6EFE3", "#0F4C6B", "#C8A15A"],
    fonts: "Cormorant Garamond · Inter",
    path: "/concept-d",
    accent: "bg-[#0F4C6B]",
    text: "text-[#0F4C6B]",
    tile: "bg-[#FBF7F0] border border-[#0F4C6B]/15",
    Icon: ShieldCheck,
  },
  {
    id: "e",
    num: "05",
    eyebrow: "Concept 05 · Founder-forward production build",
    title: "Final",
    summary:
      "Faith 1st — Leadership, People & Culture Consulting. Concept 4's warm palette + refinement, Concept 1's disciplined layout, and Concept 2's lower-page thought-leadership shelf — merged into one production system. Six services (People & Leadership Consulting, Supervisory Training & Workshops, Executive & Leadership Coaching, Culture/Conduct/Facilitation, Speaking & Keynotes, Strategic People Advisory) and a consultation-led conversion path.",
    role: "Production website",
    palette: ["#D8D4CB", "#EEE8DD", "#123F57", "#A88A57"],
    fonts: "Cormorant Garamond · Inter",
    path: "/concept-e",
    accent: "bg-[#123F57]",
    text: "text-[#123F57]",
    tile: "bg-[#EEE8DD] border border-[#123F57]/15",
    Icon: ShieldCheck,
  },
  {
    id: "f",
    num: "06",
    eyebrow: "Concept 06 · Firm-led agency system",
    title: "Faith 1st Consulting",
    summary:
      "A distinct agency-first re-cut merging Concept 1's disciplined hierarchy, Concept 3's boxed rhythm, and Concept 5's tone into a compartmentalized firm-led system. No founder portrait on the homepage; Katrina appears only on the About page as credibility anchor. Six pathways as side-by-side clickable tiles, a CMS-ready insights library (white papers · practice notes · field reports · guides), and the new F1C monogram + Faith 1st Consulting wordmark applied throughout.",
    role: "Firm-led production system",
    palette: ["#D8D4CB", "#EEE8DD", "#123F57", "#A88A57"],
    fonts: "Cormorant Garamond · Inter",
    path: "/concept-f",
    accent: "bg-[#123F57]",
    text: "text-[#123F57]",
    tile: "bg-[#D8D4CB] border border-[#123F57]/15",
    Icon: ShieldCheck,
  },
];

const evaluationLens = [
  {
    dim: "Primary emotional cue",
    c1: "Confidence",
    c2: "Structure",
    c3: "Trust",
  },
  {
    dim: "First impression",
    c1: "Executive maturity",
    c2: "Strategic clarity",
    c3: "Relational credibility",
  },
  {
    dim: "Strongest audience trigger",
    c1: "Seriousness under pressure",
    c2: "Need for systems and readiness",
    c3: "Need for human wisdom in complex moments",
  },
  {
    dim: "Strongest visual asset",
    c1: "Seated portrait & restrained hierarchy",
    c2: "Modular pathways & structured layout",
    c3: "Close portrait & editorial warmth",
  },
  {
    dim: "Risk if overdone",
    c1: "Could feel too formal",
    c2: "Could feel too procedural",
    c3: "Could feel too founder-led",
  },
  {
    dim: "Best strategic use",
    c1: "Institutional trust-building",
    c2: "Offer clarity and scalability",
    c3: "Distinctive high-trust differentiation",
  },
];

const buyerQuestions = [
  "Is this for someone like me?",
  "Do they understand the problem being solved?",
  "Can they be trusted?",
  "What is the next step?",
  "What happens after I inquire?",
];

export default function LandingIndex() {
  return (
    <main
      data-testid="landing-index"
      className="min-h-screen bg-[#FDFBF7] text-[#1A1A1A] font-chivo"
    >
      {/* Top bar */}
      <header className="border-b border-[#1A1A1A]/10">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 h-20 flex items-center justify-between">
          <div className="flex items-center gap-5">
            <BrandMark href="/" size="md" testId="index-wordmark" />
            <span className="hidden md:inline-flex items-center text-[10px] uppercase tracking-[0.22em] text-[#1A1A1A]/50 border-l border-[#1A1A1A]/15 pl-5">
              Homepage Concept Review
            </span>
          </div>
          <div className="flex items-center gap-4 text-[11px] uppercase tracking-[0.18em] text-[#1A1A1A]/60">
            <span className="hidden sm:inline">For executive review</span>
            <a
              href="#concepts"
              data-testid="index-jump-concepts"
              className="text-[#1A1A1A] font-medium border-b border-[#1A1A1A]/40 hover:border-[#1A1A1A] transition-colors"
            >
              View concepts
            </a>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 pt-20 pb-24 lg:pt-28 lg:pb-32">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          <div className="lg:col-span-8 fade-up">
            <p
              data-testid="index-eyebrow"
              className="text-[11px] uppercase tracking-[0.28em] text-[#B8838B] font-medium mb-8"
            >
              Three strategic directions · One conversion asset
            </p>
            <h1
              data-testid="index-title"
              className="font-general font-medium text-4xl sm:text-5xl lg:text-[68px] leading-[1.02] tracking-tight"
            >
              Three homepage directions
              <br />
              for{" "}
              <span className="italic font-spectral font-normal text-[#B8838B]">
                Faith&nbsp;1st
              </span>
              .
            </h1>
            <p
              data-testid="index-subcopy"
              className="mt-8 max-w-2xl text-[16px] sm:text-[17px] leading-[1.65] text-[#1A1A1A]/72"
            >
              The Faith&nbsp;1st homepage is the visible front door to a broader
              growth and sales system — measured by{" "}
              <strong className="text-[#1A1A1A] font-semibold">qualified inquiry</strong>,
              not traffic volume. Each direction below is a complete homepage
              built to attract one kind of right-fit buyer. Review for fit, not
              taste alone.
            </p>
          </div>

          <div className="lg:col-span-4 fade-up">
            <div className="border border-[#1A1A1A]/15 p-6 bg-white/40">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#1A1A1A]/60 mb-4">
                Strategy at a glance
              </div>
              <dl className="space-y-3 text-[13px]">
                <div className="flex justify-between gap-4">
                  <dt className="text-[#1A1A1A]/60">Primary buyer</dt>
                  <dd className="text-[#1A1A1A] text-right">ED / CEO / COO / HR / Board</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-[#1A1A1A]/60">Org size fit</dt>
                  <dd className="text-[#1A1A1A] text-right">50–500 employees</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-[#1A1A1A]/60">Primary conversion</dt>
                  <dd className="text-[#1A1A1A] text-right">Qualified inquiry</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-[#1A1A1A]/60">Posture</dt>
                  <dd className="text-[#1A1A1A] text-right">Front door · not funnel</dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
      </section>

      {/* Five buyer questions strip */}
      <section className="border-y border-[#1A1A1A]/10 bg-[#FAF6EE]">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 py-12">
          <div className="flex flex-wrap items-center gap-4 lg:gap-6">
            <p className="text-[11px] uppercase tracking-[0.28em] text-[#B8838B] font-medium mr-auto">
              Every concept must help a right-fit buyer answer ↓
            </p>
            {buyerQuestions.map((q, i) => (
              <span
                key={i}
                data-testid={`buyer-question-${i}`}
                className="text-[12.5px] text-[#1A1A1A]/85 bg-white border border-[#1A1A1A]/12 px-3.5 py-1.5 rounded-full"
              >
                <span className="text-[#B8838B] font-medium mr-1.5">0{i + 1}</span>
                {q}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Concept cards */}
      <section id="concepts" data-testid="index-concepts">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 py-20 lg:py-24">
          <div className="flex items-end justify-between mb-12 lg:mb-16 gap-6 flex-wrap">
            <div>
              <p className="text-[11px] uppercase tracking-[0.28em] text-[#B8838B] font-medium mb-3">
                The three directions
              </p>
              <h2 className="font-general font-medium text-3xl sm:text-4xl tracking-tight max-w-2xl leading-tight">
                Open each concept as its own homepage. Compare them through the
                evaluation lens below — not visual preference alone.
              </h2>
            </div>
            <div className="text-[12px] text-[#1A1A1A]/60 max-w-xs">
              Each direction is a complete, conversion-ready interpretation of
              the Faith&nbsp;1st strategy.
            </div>
          </div>

          <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-5 lg:gap-6">
            {concepts.map((c) => {
              const Icon = c.Icon;
              return (
                <Link
                  key={c.id}
                  to={c.path}
                  data-testid={`index-concept-card-${c.id}`}
                  className={`group relative ${c.tile} p-7 lg:p-8 flex flex-col min-h-[480px] hover:-translate-y-1 transition-all duration-500`}
                >
                  <div className="flex items-start justify-between mb-8">
                    <span
                      className={`inline-flex items-center justify-center w-11 h-11 rounded-full ${c.accent} text-white font-general font-medium text-[14px]`}
                    >
                      {c.num}
                    </span>
                    <Icon size={22} weight="duotone" className={c.text} />
                  </div>

                  <p
                    className={`text-[10px] uppercase tracking-[0.22em] ${c.text} font-medium mb-4`}
                  >
                    {c.eyebrow}
                  </p>
                  <h3
                    className={`font-general font-medium text-[28px] leading-[1.1] tracking-tight mb-4 ${c.text}`}
                  >
                    {c.title}
                  </h3>
                  <p className="text-[14px] leading-[1.65] text-[#1A1A1A]/72 mb-6">
                    {c.summary}
                  </p>

                  <div className="mt-auto pt-6 border-t border-[#1A1A1A]/10 space-y-3 text-[12px]">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-[#1A1A1A]/50 uppercase tracking-[0.15em] text-[10px]">
                        Primary strength
                      </span>
                      <span className="text-[#1A1A1A] text-right">{c.role}</span>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-[#1A1A1A]/50 uppercase tracking-[0.15em] text-[10px]">
                        Typography
                      </span>
                      <span className="text-[#1A1A1A] text-right">{c.fonts}</span>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-[#1A1A1A]/50 uppercase tracking-[0.15em] text-[10px]">
                        Palette
                      </span>
                      <div className="flex gap-1.5">
                        {c.palette.map((p, idx) => (
                          <span
                            key={idx}
                            className="w-4 h-4 rounded-full border border-black/10"
                            style={{ background: p }}
                          />
                        ))}
                      </div>
                    </div>
                  </div>

                  <div
                    className={`mt-6 inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.18em] font-medium ${c.text} group-hover:gap-3 transition-all`}
                  >
                    Open homepage <ArrowUpRight size={14} weight="bold" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Evaluation lens (replaces Execution matrix) */}
      <section
        data-testid="evaluation-lens"
        className="border-t border-[#1A1A1A]/10 bg-[#FAF6EE]"
      >
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 py-20 lg:py-24">
          <div className="grid lg:grid-cols-12 gap-10 mb-12">
            <div className="lg:col-span-5">
              <p className="text-[11px] uppercase tracking-[0.28em] text-[#B8838B] font-medium mb-4">
                Evaluation lens
              </p>
              <h2 className="font-general font-medium text-3xl sm:text-4xl tracking-tight leading-tight">
                Which direction best helps a high-fit buyer self-identify, trust
                Faith&nbsp;1st, and move toward inquiry?
              </h2>
            </div>
            <p className="lg:col-span-6 lg:col-start-7 text-[15px] text-[#1A1A1A]/70 leading-[1.7]">
              The three concepts should not be evaluated only on visual
              preference. Use this lens to identify the direction that most
              effectively converts the right buyer — the buyer Faith&nbsp;1st
              wants to win.
            </p>
          </div>

          <div className="border border-[#1A1A1A]/15 bg-white overflow-hidden">
            {/* Header row */}
            <div className="grid grid-cols-4 border-b border-[#1A1A1A]/15 bg-[#FAF6EE]">
              <div className="px-5 py-4 text-[10px] uppercase tracking-[0.22em] text-[#1A1A1A]/55 font-medium">
                Dimension
              </div>
              {concepts.filter((c) => c.id !== "d").map((c) => (
                <div
                  key={c.id}
                  className="px-5 py-4 border-l border-[#1A1A1A]/12"
                >
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#B8838B] font-medium">
                    Concept {c.num}
                  </div>
                  <div className="text-[13px] font-medium text-[#1A1A1A] mt-1">
                    {c.title}
                  </div>
                </div>
              ))}
            </div>
            {/* Rows */}
            {evaluationLens.map((row, i) => (
              <div
                key={i}
                data-testid={`lens-row-${i}`}
                className={`grid grid-cols-4 ${
                  i < evaluationLens.length - 1
                    ? "border-b border-[#1A1A1A]/10"
                    : ""
                }`}
              >
                <div className="px-5 py-5 text-[12px] uppercase tracking-[0.14em] text-[#1A1A1A]/65 self-center">
                  {row.dim}
                </div>
                <div className="px-5 py-5 border-l border-[#1A1A1A]/10 text-[13.5px] text-[#1A1A1A]/85 leading-snug">
                  {row.c1}
                </div>
                <div className="px-5 py-5 border-l border-[#1A1A1A]/10 text-[13.5px] text-[#1A1A1A]/85 leading-snug">
                  {row.c2}
                </div>
                <div className="px-5 py-5 border-l border-[#1A1A1A]/10 text-[13.5px] text-[#1A1A1A]/85 leading-snug">
                  {row.c3}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#1A1A1A]/10 bg-[#1A1A1A] text-[#FDFBF7]">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 py-14 flex flex-wrap items-start justify-between gap-8">
          <div>
            <BrandMark variant="dark" size="md" />
            <p className="text-[12px] text-[#FDFBF7]/60 mt-3 max-w-xs">
              {TAGLINE}
            </p>
          </div>
          <div className="flex items-center gap-6 text-[11px] uppercase tracking-[0.22em] text-[#FDFBF7]/55 flex-wrap">
            <Link to="/concept-a" data-testid="footer-link-a" className="hover:text-[#FDFBF7]">
              Concept 01
            </Link>
            <Link to="/concept-b" data-testid="footer-link-b" className="hover:text-[#FDFBF7]">
              Concept 02
            </Link>
            <Link to="/concept-c" data-testid="footer-link-c" className="hover:text-[#FDFBF7]">
              Concept 03
            </Link>
            <Link to="/concept-d" data-testid="footer-link-d" className="hover:text-[#FDFBF7] text-[#B8838B]">
              Concept 04 · Synthesis →
            </Link>
          </div>
        </div>
      </footer>
    </main>
  );
}
